/**
 * Publishing a course out of the vault.
 *
 * ── What this does, in one line ─────────────────────────────────────────────
 * Downloads the encrypted `.zip` node, decrypts it in the browser, unpacks it,
 * and uploads the files as **plaintext** to the course origin's R2 prefix.
 *
 * ── Why the unzip happens here ──────────────────────────────────────────────
 * Two reasons, and the second is the important one.
 *
 * The package is encrypted, so the server could not open it even if it wanted
 * to — the key never leaves this browser. And unzipping server-side would put an
 * attacker-controlled archive in a Worker's memory, writing whatever paths it
 * names. Doing it here means the path list arrives at the server one entry at a
 * time and is checked against a whitelist that cannot express `..`.
 *
 * ── Why there is no zip library ─────────────────────────────────────────────
 * The CSP on this origin names no host but our own, so nothing loads from a CDN
 * — but a bundled dependency would be fine. It is not here because it is not
 * needed: a Compose export is a flat archive of stored and deflated entries, and
 * `DecompressionStream('deflate-raw')` is native. The reader below is about
 * eighty lines and has no supply chain.
 */

import { api } from './api';
import { downloadStream, type DecryptedNode } from './nodes';

// ── a minimal ZIP reader ────────────────────────────────────────────────────

/** One entry of the central directory. */
export interface ZipEntry {
  path: string;
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
}

const EOCD_SIG = 0x06054b50;
const CEN_SIG = 0x02014b50;

/**
 * Read the central directory.
 *
 * Central directory rather than a scan of local headers: local headers may carry
 * sizes of zero with the real values in a trailing data descriptor, which cannot
 * be read forwards without decompressing to find out where the entry ends. The
 * central directory always has the true sizes.
 */
export function readDirectory(buf: ArrayBuffer): ZipEntry[] {
  const view = new DataView(buf);

  // The end-of-central-directory record is last, but may be followed by up to
  // 64 KiB of comment, so it has to be searched for backwards.
  let eocd = -1;
  const from = Math.max(0, buf.byteLength - 65_557);
  for (let i = buf.byteLength - 22; i >= from; i--) {
    if (view.getUint32(i, true) === EOCD_SIG) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('That file is not a zip archive.');

  const count = view.getUint16(eocd + 10, true);
  let at = view.getUint32(eocd + 16, true);

  const entries: ZipEntry[] = [];
  const dec = new TextDecoder();

  for (let i = 0; i < count; i++) {
    if (view.getUint32(at, true) !== CEN_SIG) break;

    const method = view.getUint16(at + 10, true);
    const compressedSize = view.getUint32(at + 20, true);
    const uncompressedSize = view.getUint32(at + 24, true);
    const nameLen = view.getUint16(at + 28, true);
    const extraLen = view.getUint16(at + 30, true);
    const commentLen = view.getUint16(at + 32, true);
    const localHeaderOffset = view.getUint32(at + 42, true);
    const path = dec.decode(new Uint8Array(buf, at + 46, nameLen));

    // Directory entries end in `/` and carry no data. Skipped rather than
    // created: R2 has no directories, and the paths carry the structure.
    if (!path.endsWith('/')) {
      entries.push({ path, method, compressedSize, uncompressedSize, localHeaderOffset });
    }
    at += 46 + nameLen + extraLen + commentLen;
  }

  return entries;
}

/** The bytes of one entry, decompressed if it needs to be. */
export async function readEntry(buf: ArrayBuffer, entry: ZipEntry): Promise<Uint8Array> {
  const view = new DataView(buf);
  // The local header's name and extra lengths can differ from the central
  // directory's, so the data offset has to come from the local header.
  const nameLen = view.getUint16(entry.localHeaderOffset + 26, true);
  const extraLen = view.getUint16(entry.localHeaderOffset + 28, true);
  const start = entry.localHeaderOffset + 30 + nameLen + extraLen;
  const raw = new Uint8Array(buf, start, entry.compressedSize);

  if (entry.method === 0) return raw; // stored
  if (entry.method !== 8) throw new Error(`Unsupported compression in ${entry.path}.`);

  // `deflate-raw`, not `deflate`: zip entries carry no zlib header.
  const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// ── publishing ──────────────────────────────────────────────────────────────

export interface PublishProgress {
  /** 0–100 across the whole operation, so one bar can carry all three phases. */
  percent: number;
  phase: 'downloading' | 'unpacking' | 'uploading' | 'publishing';
  detail?: string;
}

/**
 * Publish a `.zip` course package from the vault.
 *
 * The course is created first, uploaded second and made live last. That order
 * is deliberate: a course that went live before its last file arrived would be a
 * broken link that someone may already have shared.
 */
export async function publishCourse(
  node: DecryptedNode,
  nodeKey: CryptoKey,
  title: string,
  onProgress: (p: PublishProgress) => void
): Promise<{ id: string; slug: string }> {
  onProgress({ percent: 2, phase: 'downloading' });

  const stream = await downloadStream(node.id, nodeKey, (done, total) => {
    onProgress({
      percent: 2 + Math.round((done / Math.max(1, total)) * 28),
      phase: 'downloading'
    });
  });
  // The whole archive in memory: the reader needs to seek backwards to the
  // central directory, which a stream cannot do. Bounded by the upload limit
  // rather than unbounded — a course package is tens of megabytes.
  const encrypted = await new Response(stream).arrayBuffer();

  onProgress({ percent: 32, phase: 'unpacking' });
  const entries = readDirectory(encrypted);

  if (!entries.length) throw new Error('That package is empty.');
  if (!entries.some((e) => e.path === 'index.html')) {
    throw new Error(
      'That package has no index.html at its root, so there would be nothing to open. ' +
        'Export from Compose as HTML5 or SCORM and upload the .zip it produces.'
    );
  }

  const { id, slug } = await api.courses.create({ title, sourceNodeId: node.id });

  // Sequential rather than parallel. A course is dozens of small files, the
  // Worker charges one D1 write per file, and forty concurrent PUTs against a
  // counter that each one increments is a good way to lose the count.
  let done = 0;
  for (const entry of entries) {
    const bytes = await readEntry(encrypted, entry);
    await api.courses.putFile(id, entry.path, bytes);
    done += 1;
    onProgress({
      percent: 35 + Math.round((done / entries.length) * 60),
      phase: 'uploading',
      detail: entry.path
    });
  }

  onProgress({ percent: 97, phase: 'publishing' });
  await api.courses.publish(id);

  onProgress({ percent: 100, phase: 'publishing' });
  return { id, slug };
}

/**
 * The public address of a course.
 *
 * Derived from the vault's own origin rather than hardcoded, so a staging
 * deployment produces staging links: `cloud.` becomes `courses.`, and anything
 * that is not a `cloud.` host falls back to the production hostname rather than
 * inventing one.
 */
export function courseUrl(slug: string): string {
  const host = window.location.hostname;
  const courses = host.startsWith('cloud.')
    ? `courses.${host.slice('cloud.'.length)}`
    : 'courses.mauvely.com';
  return `${window.location.protocol}//${courses}/c/${slug}/`;
}
